/**
 * WebSocket-related constants
 *
 * These enums provide runtime-accessible values for WebSocket close codes
 * and message types used across the application.
 */

/**
 * Custom WebSocket close codes enum
 * Standard codes: 1000-1015 (RFC 6455)
 * Custom codes: 4000-4999 (application-specific)
 */
export enum WsCloseCodeEnum {
  // Standard codes
  NORMAL_CLOSURE = 1000,
  GOING_AWAY = 1001,

  // Custom application codes
  NOT_PARTICIPANT = 4003,
  SESSION_NOT_FOUND = 4004,
  UNAUTHORIZED = 4401,
  FORBIDDEN = 4403,
  // Old socket displaced by a NEWER connection from the SAME user (same-session
  // reconnect: StrictMode double-mount, fast nav, network blip). Benign — the
  // client ignores it (no redirect, no reconnect). Distinct from
  // SIGNED_OUT_ELSEWHERE (4411), which is a hard kick to /dashboard.
  CONNECTION_SUPERSEDED = 4409,
  SIGNED_OUT_ELSEWHERE = 4411,
  TOKEN_EXPIRED = 4419,

  INSUFFICIENT_TOKENS = 4402,

  // Game-specific codes
  GAME_ENDED = 4100,
  PLAYER_KICKED = 4101,
}

/**
 * Game WebSocket message types enum (server → client)
 */
export enum GameMessageTypeEnum {
  // Connection
  CONNECTED = 'connected',
  ERROR = 'error',
  PONG = 'pong',

  // Session lifecycle
  GAME_STATE = 'game_state',
  SESSION_ENDED = 'session_ended',
  RECONNECT_SUCCESS = 'reconnect_success',
  GRACE_PERIOD_STATUS = 'grace_period_status',

  // Round lifecycle
  PLAYER_READY = 'player_ready',
  ALL_PLAYERS_READY = 'all_players_ready',

  // Scene
  SCENE_TRANSITION = 'scene_transition',
  SCENE_IMAGE_READY = 'scene_image_ready',
  SCENE_STATE = 'scene_state',

  // Narrative
  NARRATIVE_STREAM = 'narrative_stream',
  CHAT_BROADCAST = 'chat_broadcast',

  // Character updates
  CHARACTER_STATUS = 'character_status',
  CHARACTER_XP = 'character_xp',
  DEATH_SAVE_RESULT = 'death_save_result',
  TPK_STATUS = 'tpk_status',

  // Saved state
  SAVED_STATE = 'saved_state',
  CHARACTER_RESTORED = 'character_restored',
}

/**
 * Game WebSocket message types enum (client → server)
 */
export enum GameClientMessageTypeEnum {
  PING = 'ping',
  PLAYER_READY = 'player_ready',
  PLAYER_ACTION = 'player_action',
  CHAT_MESSAGE = 'chat_message',
}

/**
 * Lobby WebSocket message types enum (commonly used)
 */
export enum LobbyMessageTypeEnum {
  // Session state
  SESSION_STATE = 'lobby:session-state',
  SERVER_INFO = 'lobby:server-info',

  // NPC communication
  TAVIAN_RESPONSE = 'lobby:tavian-response',
  NPC_STREAM_START = 'lobby:npc-stream-start',
  NPC_STREAM_CHUNK = 'lobby:npc-stream-chunk',
  NPC_STREAM_END = 'lobby:npc-stream-end',

  // Character creation
  CHAR_CREATE_RESPONSE = 'lobby:char-create-response',
  CHAR_CREATE_AVAILABLE = 'lobby:char-create-available',

  // Level up
  LEVEL_UP_RESPONSE = 'lobby:level-up-response',
  LEVEL_UP_AVAILABLE = 'lobby:level-up-available',

  // Party
  PARTY_CREATED = 'lobby:party-created',
  PARTY_UPDATED = 'lobby:party-updated',
  PARTY_CHAT = 'lobby:party-chat',
  PARTY_DECISION_PROPOSED = 'lobby:party-decision-proposed',
  PARTY_DECISION_VOTE = 'lobby:party-decision-vote',
  PARTY_DECISION_RESOLVED = 'lobby:party-decision-resolved',

  // Game transition
  GAME_STARTED = 'lobby:game-started',

  // Errors
  ERROR = 'error',
}
